// #region Imports
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { skipToken } from "@reduxjs/toolkit/query";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import TagInput from "_shared/_views/tags/tag-input";
import DebouncedTextField from "_shared/_views/text-input/debounced-text-field";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import FeeScheduleCategory from "_shared/fee-schedules/categories/fee-schedule-category";
import FeeScheduleCategoryName from "_shared/fee-schedules/categories/names/fee-schedule-category-name";
import FeeScheduleTag from "_shared/fee-schedules/tags/fee-schedule-tag";
import Guid from "_shared/guids/guid";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { FeeScheduleCategoryMaskedCurrency } from "work/_shared/masked-fields";
import {
  useCreateFeeScheduleTemplateMutation,
  useDeleteFeeScheduleTemplateMutation,
  useGetFeeScheduleTemplateByIdQuery,
  useUpdateFeeScheduleTemplateMutation,
} from "work/fee-schedule-templates/_api/fee-schedule-template-api";
import { FeeScheduleTemplateJSON } from "work/fee-schedule-templates/_schemas/fee-schedule-template-schema";
import {
  getFeeScheduleTemplateById,
  isFeeScheduleTemplateLoadIssue,
} from "work/fee-schedule-templates/_store/fee-schedule-templates-redux-slice";
import FeeScheduleTemplatesTable from "work/fee-schedule-templates/_views/tables/fee-schedule-templates-table";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import EditFeeScheduleCategoryDrawer from "_shared/fee-schedules/categories/_views/templates/edit-fee-schedule-category-drawer";
import ExistingFeeScheduleCategoryDrawer from "_shared/fee-schedules/categories/_views/templates/existing-fee-schedule-category-drawer";
import ExistingFeeScheduleTemplateDrawer from "_shared/fee-schedules/categories/_views/templates/existing-fee-schedule-template-drawer";
import FeeSchedule from "work/work-agreements/fee-schedules/fee-schedule";
import { HttpStatusCode } from "axios";
// #endregion

function isFetchBaseQueryError(error: unknown): error is FetchBaseQueryError {
  return typeof error === "object" && error !== null && "status" in error;
}

function getErrorMessage(data: unknown): string | undefined {
  if (typeof data === "string" && data.trim().length > 0) {
    return data;
  }

  if (Array.isArray(data)) {
    return data.find((value): value is string => typeof value === "string" && value.trim().length > 0);
  }

  if (typeof data !== "object" || data === null) {
    return undefined;
  }

  const errorData = data as Record<string, unknown>;
  const messageFields = ["message", "error", "title", "detail"] as const;

  for (const field of messageFields) {
    if (field in errorData) {
      const value = errorData[field];
      if (typeof value === "string" && value.trim().length > 0) {
        return value;
      }
    }
  }

  const prioritizedFieldNames = ["name"] as const;
  for (const field of prioritizedFieldNames) {
    const value = errorData[field];
    if (Array.isArray(value)) {
      const message = value.find((entry): entry is string => typeof entry === "string" && entry.trim().length > 0);
      if (message) {
        return message;
      }
    }
  }

  for (const value of Object.values(errorData)) {
    if (Array.isArray(value)) {
      const message = value.find((entry): entry is string => typeof entry === "string" && entry.trim().length > 0);
      if (message) {
        return message;
      }
    }
  }

  return undefined;
}

function getSaveConflictMessage(error: unknown): string | undefined {
  if (isFetchBaseQueryError(error) && error.status === HttpStatusCode.BadRequest) {
    return (
      getErrorMessage(error.data) ?? "Unable to save template at this time, please check all fields and try again."
    );
  }

  return undefined;
}

// #region Styled Components
const PageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  height: "100%",
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "row",
  justifyContent: "space-between",
  padding: theme.spacing(3),
  paddingBottom: 0,
}));

const TitleContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
}));

export const Header = styled("section")(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  paddingBottom: theme.spacing(1),
  paddingLeft: theme.spacing(3),
  position: "sticky",
  top: "0px",
  zIndex: 10,
}));

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(2),
}));

export const ActionsContainer = styled("section")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    paddingBottom: theme.spacing(1),
    paddingRight: 0,
    paddingTop: theme.spacing(1),
  },
  backgroundColor: theme.palette.background.default,
  bottom: "0px",
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  padding: theme.spacing(0, 3),
  position: "sticky",
  width: "100%",
  zIndex: 10,
}));

export const Actions = styled("section")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    flexWrap: "nowrap",
  },
  alignContent: "end",
  alignItems: "center",
  display: "grid",
  flex: 1,
  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  gap: theme.spacing(1),
  minHeight: "64px",
  width: "100%",
  "& > span": {
    width: "100%",
  },
}));

export const ActionButton = styled(LoadingButton)(({ theme }) => ({
  minWidth: theme.spacing(24),
  whiteSpace: "nowrap",
  width: "100%",
  "&.Mui-disabled": {
    color: "rgba(0, 0, 0, 0.26) !important",
    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
  },
}));

const Main = styled("main", {
  shouldForwardProp: (prop) => prop !== "open",
})<{
  open?: boolean;
}>(({ theme, open }) => ({
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
  },
  display: "flex",
  height: "100%",
  flexDirection: "column",
  flex: 1,
  transition: theme.transitions.create(["margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: open ? 0 : "-400px",
  position: "relative",
  width: "100%",
  variants: [
    {
      props: ({ open }) => open,
      style: {
        transition: theme.transitions.create(["margin"], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginRight: 0,
      },
    },
  ],
}));

export const Content = styled("section")(({ theme }) => ({
  alignItems: "center",
  flex: 1,
  flexDirection: "row",
  height: "100%",
  marginTop: theme.spacing(2),
  minHeight: "25rem",
  overflow: "auto",
  width: "100%",
}));

export const PageContent = styled("section")(() => ({
  alignItems: "stretch",
  flexDirection: "column",
  justifyContent: "center",
  display: "flex",
  flex: 1,
  height: "100%",
  overflow: "visible",
  width: "100%",
}));

const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

const DetailsContainer = styled(Grid)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(5),
  marginBottom: theme.spacing(3),
  marginTop: theme.spacing(1),
  padding: theme.spacing(0, 3),
  width: "100%",
  "& > .MuiTextField-root": {
    width: "50%",
  },
}));

const TemplatesTable = styled(FeeScheduleTemplatesTable)(() => ({
  height: "100%",
}));
// #endregion

type FeeScheduleTemplateAction = "edit" | "create" | "view";
const FeeScheduleTemplateActions = new Set<FeeScheduleTemplateAction>(["edit", "create", "view"]);
const CATEGORY_ALERT_BANNER_TIMEOUT_MS = 5000;
const DRAWER_LAYOUT_TRANSITION_TIMEOUT_MS = 250;

export default function FeeScheduleTemplatePage() {
  // #region Refs
  const nameFieldRef = useRef<HTMLInputElement>(null);
  const hydratedTemplateIdRef = useRef<string | null>(null);

  // #region Hooks
  const location = useLocation();
  const session = useSession();
  const navigate = useNavigate();
  const { action: actionParam, id: idParam } = useParams();
  const dispatch = useAttorneyHubDispatch();
  const confirm = useConfirmDialog();

  // #region Route Info
  const feeScheduleTemplateId = useMemo(() => (idParam ? new Guid(idParam) : undefined), [idParam]);
  const pageAction: FeeScheduleTemplateAction = (actionParam as FeeScheduleTemplateAction) ?? "create";

  // Unsupported action
  if (!FeeScheduleTemplateActions.has(pageAction)) {
    throw new Response("Not Found", { status: 404 });
  }
  // Fee schedule template id is required for edit/view
  if (pageAction !== "create" && !idParam) {
    throw new Response("Not Found", { status: 404 });
  }

  // #region Permissions Queries
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);

  // #region Fee Schedule Template Queries
  const feeScheduleTemplateJson = useSelector(getFeeScheduleTemplateById(feeScheduleTemplateId));
  const feeScheduleTemplate = useMemo(() => {
    if (!feeScheduleTemplateJson || isFeeScheduleTemplateLoadIssue(feeScheduleTemplateJson) || !session.user)
      return null;
    return FeeScheduleTemplate.fromJSON(feeScheduleTemplateJson);
  }, [feeScheduleTemplateJson, session.user]);

  const feeScheduleTemplateQuery = useGetFeeScheduleTemplateByIdQuery(
    !feeScheduleTemplateId || feeScheduleTemplateId.isEmpty
      ? skipToken
      : { feeScheduleTemplateId: feeScheduleTemplateId?.value ?? Guid.EMPTY.value },
  );

  // #region State Variables
  const [isAddFeePanelOpen, setIsAddFeePanelOpen] = useState<boolean>(false);
  const [isExistingFeePanelOpen, setIsExistingFeePanelOpen] = useState<boolean>(false);
  const [isExistingTemplatePanelOpen, setIsExistingTemplatePanelOpen] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [categoryAlertBannerMessage, setCategoryAlertBannerMessage] = useState<string | undefined>(undefined);

  const [name, setName] = useState<string>(feeScheduleTemplate?.name ?? "");
  const [tags, setTags] = useState<FeeScheduleTag[]>([]);
  const [categories, setCategories] = useState<Array<FeeScheduleCategory> | Array<FeeScheduleCategoryMaskedCurrency>>(
    feeScheduleTemplate?.categories ?? [],
  );
  const [currentCategory, setCurrentCategory] = useState<
    FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency | undefined
  >(undefined);
  // #endregion

  // #region Form State
  const isLoading = feeScheduleTemplateQuery.isLoading || sessionPermissionsLoading;
  const disableEditing = pageAction === "view" || !pageAction;
  const isFormValid = Boolean(name) && categories.length > 0;
  const disableSave = isLoading || disableEditing || !isFormDirty || !isFormValid;
  const isAnyDrawerOpen = isAddFeePanelOpen || isExistingFeePanelOpen || isExistingTemplatePanelOpen;

  const [createFeeScheduleTemplateQuery] = useCreateFeeScheduleTemplateMutation();
  const [updateFeeScheduleTemplateQuery] = useUpdateFeeScheduleTemplateMutation();
  const [deleteFeeScheduleTemplateQuery] = useDeleteFeeScheduleTemplateMutation();

  // #region Lifecycle
  useEffect(() => {
    // Fee schedule template invalid/not found
    const shouldShowNotFound =
      Boolean(feeScheduleTemplateId) &&
      !feeScheduleTemplateId?.isEmpty &&
      feeScheduleTemplate === null &&
      pageAction !== "create" &&
      !isLoading;

    if (shouldShowNotFound) {
      enqueueSnackbar("Fee schedule template not found", { variant: "warning" });
      navigate("/fee-schedules/templates", { replace: true });
    }
  }, [pageAction, navigate, feeScheduleTemplate, feeScheduleTemplateId, isLoading]);

  useEffect(() => {
    if (pageAction === "create") return;
    if (!feeScheduleTemplate?.id) return;
    if (isFormDirty) return;

    const templateId = feeScheduleTemplate.id.value;
    if (hydratedTemplateIdRef.current === templateId) return;

    setName(feeScheduleTemplate.name ?? "");
    setTags(feeScheduleTemplate.tags ?? []);
    setCategories(feeScheduleTemplate.categories ?? []);

    hydratedTemplateIdRef.current = templateId;
  }, [pageAction, feeScheduleTemplate, isFormDirty]);

  useEffect(() => {
    if (!categoryAlertBannerMessage) return;

    const timeoutId = globalThis.setTimeout(() => {
      setCategoryAlertBannerMessage(undefined);
    }, CATEGORY_ALERT_BANNER_TIMEOUT_MS);

    return () => globalThis.clearTimeout(timeoutId);
  }, [categoryAlertBannerMessage]);

  useEffect(() => {
    globalThis.dispatchEvent(new Event("resize"));

    const timeoutId = globalThis.setTimeout(() => {
      globalThis.dispatchEvent(new Event("resize"));
    }, DRAWER_LAYOUT_TRANSITION_TIMEOUT_MS);

    return () => globalThis.clearTimeout(timeoutId);
  }, [isAnyDrawerOpen]);

  // Permissions bootstrap
  useEffect(() => {
    if (!sessionPermissions && !sessionPermissionsError && !sessionPermissionsLoading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  // #region Functions
  function getIsCategoryAlreadySelected(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency): boolean {
    return categories.some((c) => c.isEqualTo(category));
  }

  const buildTemplate = useCallback((): FeeScheduleTemplate => {
    if (!session.user?.id) {
      throw new Error("User ID is required to build fee schedule template");
    }
    if (!session.user.legalEntityId) {
      throw new Error("Legal Entity ID is required to build fee schedule template");
    }

    if (!feeScheduleTemplateId) {
      return new FeeScheduleTemplate({
        name,
        tags,
        categories,
        creator: new EntityMember({
          userId: session.user.id,
          legalEntityId: session.user.legalEntityId,
        }),
      });
    }

    return new FeeScheduleTemplate({
      id: feeScheduleTemplateId,
      name,
      tags,
      categories,
      creator: new EntityMember({
        userId: session.user.id,
        legalEntityId: session.user.legalEntityId,
      }),
    });
  }, [categories, feeScheduleTemplateId, name, session.user, tags]);

  const closeDrawers = useCallback(() => {
    setCurrentCategory(undefined);
    setIsAddFeePanelOpen(false);
    setIsExistingFeePanelOpen(false);
    setIsExistingTemplatePanelOpen(false);
  }, []);

  function handleCreateCategory() {
    setCurrentCategory(undefined);
    setIsExistingFeePanelOpen(false);
    setIsExistingTemplatePanelOpen(false);
    setIsAddFeePanelOpen(true);
  }

  function handleEditCategory(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) {
    setCurrentCategory(category);
    setIsExistingFeePanelOpen(false);
    setIsExistingTemplatePanelOpen(false);
    setIsAddFeePanelOpen(true);
  }

  function handleAddExistingCategory() {
    setCurrentCategory(undefined);
    setIsAddFeePanelOpen(false);
    setIsExistingTemplatePanelOpen(false);
    setIsExistingFeePanelOpen(true);
  }

  function handleSelectExistingTemplate() {
    setCurrentCategory(undefined);
    setIsAddFeePanelOpen(false);
    setIsExistingFeePanelOpen(false);
    setIsExistingTemplatePanelOpen(true);
  }

  function handleExistingCategoriesSelected(
    selectedCategories: Array<FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency>,
  ) {
    const { duplicateCategories, uniqueCategories } = FeeSchedule.splitCategoriesByDuplicate(
      categories,
      selectedCategories,
    );
    const nextCategories = [...categories, ...uniqueCategories];

    setCategories(nextCategories);

    if (duplicateCategories.length > 0) {
      setCategoryAlertBannerMessage(
        "One or more categories from the selected template were not added because categories with the same name already exist in the fee schedule",
      );
    } else {
      setCategoryAlertBannerMessage(undefined);
    }

    setCurrentCategory(undefined);
    setIsFormDirty(true);
    setIsExistingFeePanelOpen(false);
  }

  /**
   * Handles when a category is added
   * @param category The category to add
   */
  async function handleCategoryAdded(category: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency) {
    if (getIsCategoryAlreadySelected(category)) {
      enqueueSnackbar("Fee category is already in the fee schedule", { variant: "warning" });
      return;
    }
    setCategoryAlertBannerMessage(undefined);
    setCategories((prevCategories) => [...prevCategories, category]);
    setCurrentCategory(undefined);
    setIsFormDirty(true);
    setIsAddFeePanelOpen(false);
  }

  /**
   * Handles when a category is removed
   * @param name The name of the category to remove
   */
  async function handleCategoryRemoved(name: FeeScheduleCategoryName) {
    const updatedCategories = categories.filter((category) => category.name?.isEqualTo(name) === false);
    setCategoryAlertBannerMessage(undefined);
    setCategories(updatedCategories);
    setIsFormDirty(true);
  }

  /**
   * Handles when a category is updated
   * @param originalName The original name of the category
   * @param updatedCategory The updated fee category
   */
  async function handleCategoryUpdated(
    originalName: FeeScheduleCategoryName | null,
    updatedCategory: FeeScheduleCategory | FeeScheduleCategoryMaskedCurrency,
  ) {
    if (getIsCategoryAlreadySelected(updatedCategory)) {
      enqueueSnackbar("Fee category is already in the fee schedule", { variant: "warning" });
      return;
    }

    const updatedCategories = [...categories].filter(
      (category) => originalName && !category.name?.isEqualTo(originalName),
    );
    updatedCategories.push(updatedCategory);

    setCategoryAlertBannerMessage(undefined);
    setCategories(updatedCategories);
    setIsFormDirty(true);
    setCurrentCategory(undefined);
    setIsAddFeePanelOpen(false);
  }

  async function handleExistingTemplateSelected(template: FeeScheduleTemplate) {
    if (categories.length === 0) {
      setCategoryAlertBannerMessage(undefined);
      setCategories(template.categories);
      setIsFormDirty(true);
      setIsExistingTemplatePanelOpen(false);
      return;
    }

    const response = await confirm({
      title: "Replace Or Add Categories?",
      message: `Do you want to replace the current categories with the selected template's categories or add the selected template's categories to the current categories?`,
      okButtonText: "Replace",
      alternativeOkText: "Add",
      cancelButtonText: "Cancel",
    });

    if (response === ConfirmResponse.Cancel) return;

    if (response === ConfirmResponse.Ok) {
      setCategoryAlertBannerMessage(undefined);
      setCategories(template.categories);
      setIsFormDirty(true);
      setIsExistingTemplatePanelOpen(false);
    } else {
      const { duplicateCategories, uniqueCategories } = FeeSchedule.splitCategoriesByDuplicate(
        categories,
        template.categories,
      );
      setCategories((prevCategories) => [...prevCategories, ...uniqueCategories]);

      if (duplicateCategories.length > 0) {
        setCategoryAlertBannerMessage(
          "One or more categories from the selected template were not added because categories with the same name already exist in the fee schedule",
        );
      } else {
        setCategoryAlertBannerMessage(undefined);
      }

      setIsFormDirty(true);
      setIsExistingTemplatePanelOpen(false);
    }
  }

  async function handleDeleteClicked() {
    if (!feeScheduleTemplate?.id) {
      console.warn("Fee schedule template not found");
      return;
    }

    try {
      const deleteResponse = await confirm({
        title: "Delete Fee Schedule Template?",
        message: "This cannot be undone.",
      });

      if (deleteResponse === ConfirmResponse.Cancel) return;

      setIsDeleting(true);
      await deleteFeeScheduleTemplateQuery({ templateId: feeScheduleTemplate.id.value }).unwrap();
      enqueueSnackbar("Fee schedule template deleted", { variant: "info" });
      closeDrawers();
      navigate("/fee-schedules/templates", { replace: true });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to delete fee schedule template", { variant: "error" });
    } finally {
      setIsDeleting(false);
    }
  }

  const handleSaveTemplate = useCallback(async () => {
    try {
      let responseJSON: FeeScheduleTemplateJSON;

      if (feeScheduleTemplateId && feeScheduleTemplate) {
        responseJSON = await updateFeeScheduleTemplateQuery({
          templateId: feeScheduleTemplateId.value,
          originalTemplate: feeScheduleTemplate,
          updatedTemplate: buildTemplate(),
        }).unwrap();
      } else {
        responseJSON = await createFeeScheduleTemplateQuery({
          template: buildTemplate(),
        }).unwrap();
      }

      if (!responseJSON.id) throw new Error("Saved fee schedule template is missing an ID");

      setIsFormDirty(false);
      enqueueSnackbar(`${feeScheduleTemplateId ? "Template updated" : "Template created"} successfully`, {
        variant: "success",
      });

      if (responseJSON.id && !location.pathname.includes("/fee-schedules/templates/edit")) {
        closeDrawers();
        navigate(`/fee-schedules/templates/edit/${responseJSON.id}`, { replace: true });
      }
    } catch (error) {
      const conflictMessage = getSaveConflictMessage(error);
      if (conflictMessage) {
        enqueueSnackbar(conflictMessage, { variant: "warning" });
        return;
      }

      console.error("Error saving fee schedule template", error);
      enqueueSnackbar("Error saving fee schedule template", { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  }, [
    feeScheduleTemplateId,
    feeScheduleTemplate,
    location.pathname,
    updateFeeScheduleTemplateQuery,
    buildTemplate,
    createFeeScheduleTemplateQuery,
    closeDrawers,
    navigate,
  ]);

  // #endregion

  // #region Render
  const pageTitle = useMemo(() => {
    if (pageAction === "view") return "View";
    if (pageAction === "create") return "Create";
    if (pageAction === "edit") return "Edit Template";
    return "View";
  }, [pageAction]);

  const handleNavigateBack = useCallback(async () => {
    if (isFormDirty) {
      const response = await confirm({
        title: "Discard changes?",
        message: "Are you sure you want to discard changes?",
        okButtonText: "Discard",
        alternativeOkText: disableSave ? undefined : "Save & Close",
        cancelButtonText: "Cancel",
      });

      if (response === ConfirmResponse.Cancel) return;
      if (response === ConfirmResponse.AltOk) {
        await handleSaveTemplate();
      }
    }

    closeDrawers();
    navigate("/fee-schedules/templates", { replace: true });
  }, [closeDrawers, confirm, disableSave, handleSaveTemplate, isFormDirty, navigate]);

  if (!sessionPermissions) {
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  }
  if (!sessionPermissions.includesAccessToFeeSchedules) return <NotAuthorized />;

  return (
    <PageContainer>
      <Main open={isAnyDrawerOpen}>
        <HeaderContainer>
          <TitleContainer>
            <Chip
              icon={<ArrowBackIcon />}
              size="medium"
              label="Back"
              onClick={handleNavigateBack}
              color="primary"
              sx={{ mr: 2 }}
            />
            <Typography variant="h4">
              <span style={{ color: "gray", marginRight: "8px" }}>Fee Schedules &gt;</span>
              {pageTitle}
            </Typography>
          </TitleContainer>
          <ActionButtonsContainer>
            <LoadingButton
              variant="outlined"
              size="large"
              loading={isSaving}
              color="primary"
              disabled={isSaving || disableSave}
              startIcon={<SaveIcon />}
              onClick={handleSaveTemplate}
            >
              Save
            </LoadingButton>
          </ActionButtonsContainer>
        </HeaderContainer>
        {!disableEditing && (
          <ActionsContainer>
            <Actions>
              <ActionButton
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                loading={isDeleting}
                disabled={isLoading || isDeleting || isSaving || !feeScheduleTemplate}
                onClick={handleDeleteClicked}
              >
                Delete Template
              </ActionButton>
            </Actions>
          </ActionsContainer>
        )}
        <Content>
          <PageContent>
            {!disableEditing && (
              <DetailsContainer>
                <DebouncedTextField
                  inputRef={nameFieldRef}
                  required
                  label="Name"
                  helperText="The template's name"
                  slotProps={{
                    htmlInput: { maxLength: 50 },
                  }}
                  value={name}
                  onDebouncedChange={(newValue) => {
                    setName(newValue);
                    setIsFormDirty(true);
                  }}
                />
                <TagInput
                  helperText={`Tags to help you find this template later (ex. US, ${new AHDate().year})`}
                  onTagsUpdated={(tagValues) => {
                    setTags(tagValues.map((tagValue) => new FeeScheduleTag(tagValue)));
                    setIsFormDirty(true);
                  }}
                  onTagRemoved={(tagValue) => {
                    setTags((prevTags) => prevTags.filter((prevTag) => prevTag.value !== tagValue));
                    setIsFormDirty(true);
                  }}
                  tags={tags.map((tag) => tag.value)}
                />
              </DetailsContainer>
            )}
            <TemplatesTable
              selectedCategories={categories}
              disableEditing={disableEditing}
              disableCategorySelection={disableEditing}
              hideActionsColumn={disableEditing}
              hideCommentButton={true}
              commentsToolTip={""}
              alertBannerMessage={categoryAlertBannerMessage}
              onCreateCategoryClicked={handleCreateCategory}
              onExistingCategoryClicked={handleAddExistingCategory}
              onExistingTemplateClicked={handleSelectExistingTemplate}
              onEditCategory={handleEditCategory}
              onCategoryRemoved={handleCategoryRemoved}
            />
          </PageContent>
        </Content>
      </Main>
      <div style={{ width: isAnyDrawerOpen ? "400px" : 0 }}>
        <EditFeeScheduleCategoryDrawer
          key={currentCategory?.id.value ?? Guid.generate().value}
          category={currentCategory}
          isOpen={isAddFeePanelOpen}
          onOpen={() => setIsAddFeePanelOpen(true)}
          onClose={() => {
            setIsAddFeePanelOpen(false);
            setCurrentCategory(undefined);
          }}
          onCategoryAdded={handleCategoryAdded}
          onCategoryUpdated={handleCategoryUpdated}
        />
        <ExistingFeeScheduleCategoryDrawer
          selectedCategories={categories}
          isOpen={isExistingFeePanelOpen}
          onOpen={() => setIsExistingFeePanelOpen(true)}
          onClose={() => setIsExistingFeePanelOpen(false)}
          onCategoriesSelected={handleExistingCategoriesSelected}
        />
        <ExistingFeeScheduleTemplateDrawer
          isOpen={isExistingTemplatePanelOpen}
          onOpen={() => setIsExistingTemplatePanelOpen(true)}
          onClose={() => setIsExistingTemplatePanelOpen(false)}
          onTemplateSelected={handleExistingTemplateSelected}
        />
      </div>
    </PageContainer>
  );
}
